import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FileSignature, Users, ShieldCheck, Clock, ArrowLeft, ShieldAlert, Activity, BarChart3, MessageSquare, BookOpen, KeyRound, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { BrandLogo } from '../components/Brand';
import AgreementsInbox from '../components/AgreementsInbox';
import UnfinishedAgreements from '../components/admin/UnfinishedAgreements';
import UsersTable from '../components/admin/UsersTable';
import AdminsPanel from '../components/admin/AdminsPanel';
import ActivityLog from '../components/admin/ActivityLog';
import KnowledgePanel from '../components/admin/KnowledgePanel';
import StatsPanel from '../components/admin/StatsPanel';
import SupportInbox from '../components/admin/SupportInbox';
import CannedRepliesPanel from '../components/admin/CannedRepliesPanel';
import ChangePassword from '../components/admin/ChangePassword';
import AdminSignIn from '../components/admin/AdminSignIn';
import { SIGNED_IN_PRODUCT_NAME } from '../config/brand';

// `roles` is a UI convenience only — every endpoint re-checks the role on the server.
const TABS = [
  { key: 'support', label: 'Support', icon: MessageSquare, roles: ['admin', 'cs'] },
  { key: 'overview', label: 'Overview', icon: BarChart3, roles: ['admin'] },
  { key: 'agreements', label: 'Agreements', icon: FileSignature, roles: ['admin', 'cs'] },
  { key: 'unfinished', label: 'Unfinished', icon: Clock, roles: ['admin'] },
  { key: 'knowledge', label: 'Bot answers', icon: BookOpen, roles: ['admin'] },
  { key: 'canned', label: 'Saved replies', icon: Zap, roles: ['admin'] },
  { key: 'users', label: 'Users', icon: Users, roles: ['admin', 'cs'] },
  { key: 'activity', label: 'Activity', icon: Activity, roles: ['admin'] },
  { key: 'admins', label: 'Admins', icon: ShieldCheck, roles: ['admin'] },
  { key: 'password', label: 'My password', icon: KeyRound, roles: ['admin', 'cs'] },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const role = user?.role;
  const tabs = useMemo(() => TABS.filter((t) => t.roles.includes(role)), [role]);
  const [tab, setTab] = useState('support');

  if (loading) return null;
  if (!user) return <AdminSignIn />;

  if (!tabs.length) {
    return (
      <div className="min-h-screen bg-[#F5F6FB] flex items-center justify-center p-6" data-testid="admin-denied">
        <div className="bg-white border border-[#E3E5F5] rounded-2xl p-8 max-w-[440px] text-center">
          <ShieldAlert className="w-10 h-10 mx-auto text-[#E5387B] mb-3" />
          <h1 className="text-[20px] font-bold text-[#0A1551]">You don&apos;t have access</h1>
          <p className="text-[14px] text-[#6F76A7] mt-2">This area is limited to Cathie Wood Capital staff. Ask an admin to add {user?.email} if you need access.</p>
          <Link to="/workspace" data-testid="admin-back-workspace" className="inline-flex items-center gap-2 mt-6 px-4 py-2.5 rounded-md bg-[#0F766E] text-white text-[14px] font-semibold">
            <ArrowLeft className="w-4 h-4" /> Back to {SIGNED_IN_PRODUCT_NAME}
          </Link>
        </div>
      </div>
    );
  }

  const active = tabs.some((t) => t.key === tab) ? tab : tabs[0].key;
  const isAdmin = role === 'admin';

  return (
    <div className="min-h-screen bg-[#F5F6FB]" data-testid="admin-page">
      <header className="bg-white border-b border-[#E3E5F5] px-4 md:px-6 py-3 flex items-center gap-3">
        <Link to="/workspace" className="shrink-0"><BrandLogo size="sm" /></Link>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${isAdmin ? 'bg-[#E6F4F2] text-[#0F766E]' : 'bg-[#EEF2FF] text-[#4A517D]'}`} data-testid="admin-role-badge">
          {isAdmin ? 'Admin' : 'Customer Service'}
        </span>
        <Link to="/workspace" data-testid="admin-workspace-link" className="ml-auto inline-flex items-center gap-1.5 text-[13px] font-medium text-[#0A1551] hover:underline">
          <ArrowLeft className="w-3.5 h-3.5" /> Workspace
        </Link>
      </header>

      <nav className="bg-white border-b border-[#E3E5F5] px-2 md:px-6 flex items-center gap-1 overflow-x-auto">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
            className={`flex items-center gap-1.5 px-3 md:px-4 py-3 text-[13px] font-semibold border-b-2 whitespace-nowrap transition-colors ${
              active === key ? 'border-[#0F766E] text-[#0F766E]' : 'border-transparent text-[#6F76A7] hover:text-[#0A1551]'}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </nav>

      <main className="p-4 md:p-6 max-w-[1200px] mx-auto">
        <h1 className="text-[20px] md:text-[24px] font-bold text-[#0A1551] mb-4" data-testid="admin-title">
          {tabs.find((t) => t.key === active)?.label}
        </h1>
        {active === 'overview' && <StatsPanel />}
        {active === 'agreements' && <AgreementsInbox readOnly={!isAdmin} />}
        {active === 'unfinished' && <UnfinishedAgreements />}
        {active === 'support' && <SupportInbox />}
        {active === 'knowledge' && <KnowledgePanel />}
        {active === 'canned' && <CannedRepliesPanel />}
        {active === 'users' && <UsersTable />}
        {active === 'activity' && <ActivityLog />}
        {active === 'admins' && <AdminsPanel />}
        {active === 'password' && <ChangePassword />}
      </main>
    </div>
  );
}
